import { OWL_LISTING_ROW_COUNT, OWL_TRANSCRIPTION_SCHEMA_VERSION, OWL_WINDOW_HEIGHT, OWL_WINDOW_WIDTH, } from 'maplestory-owl-transcribe-contract';
import { FREDRICK_DATA_PROTOCOL_VERSION, FREDRICK_MARKET_VALIDATION_POLICY, FREDRICK_OWL_PAGE_CANDIDATE_TYPE, FREDRICK_OWL_PAGE_REVISION_TYPE, createEvidenceImageId, createSourceImageId, expectedOwlPageCount, expectedOwlRowsOnPage, isEvidenceImageId, isSha256Digest, isSourceImageId, transcriptionIsPartial, } from './index.js';
import { FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION, fredrickSearchClaimIsCanonical, isFredrickSearchClaimId, } from './claims.js';
import { FREDRICK_LATEST_REF_TYPE, FREDRICK_PUBLISHED_ENVELOPE_TYPE, FREDRICK_SNAPSHOT_TYPE, } from './sync.js';
export class FredrickProtocolParseError extends TypeError {
    path;
    constructor(path, message) {
        super(`${path}: ${message}`);
        this.name = 'FredrickProtocolParseError';
        this.path = path;
    }
}
function record(value, path) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new FredrickProtocolParseError(path, 'must be an object');
    }
    return value;
}
function exactKeys(value, path, required, optional = []) {
    const allowed = new Set([...required, ...optional]);
    for (const key of required) {
        if (!Object.hasOwn(value, key)) {
            throw new FredrickProtocolParseError(`${path}.${key}`, 'is required');
        }
    }
    for (const key of Object.keys(value)) {
        if (!allowed.has(key)) {
            throw new FredrickProtocolParseError(`${path}.${key}`, 'is not a protocol v1 field');
        }
    }
}
function literal(value, expected, path) {
    if (value !== expected) {
        throw new FredrickProtocolParseError(path, `must equal ${JSON.stringify(expected)}`);
    }
    return expected;
}
function stringValue(value, path, maxLength = 512) {
    if (typeof value !== 'string' || value.length === 0 || value.length > maxLength) {
        throw new FredrickProtocolParseError(path, `must be a non-empty string of at most ${maxLength} characters`);
    }
    return value;
}
function nullableString(value, path, maxLength = 512) {
    return value === null ? null : stringValue(value, path, maxLength);
}
function safeInteger(value, path, minimum = 0) {
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new FredrickProtocolParseError(path, `must be a safe integer greater than or equal to ${minimum}`);
    }
    return value;
}
function positiveInteger(value, path) {
    return safeInteger(value, path, 1);
}
function booleanValue(value, path) {
    if (typeof value !== 'boolean') {
        throw new FredrickProtocolParseError(path, 'must be a boolean');
    }
    return value;
}
function arrayValue(value, path) {
    if (!Array.isArray(value)) {
        throw new FredrickProtocolParseError(path, 'must be an array');
    }
    return value;
}
function optionalString(value, path, maxLength = 512) {
    return value === undefined ? undefined : stringValue(value, path, maxLength);
}
function parseSourceImage(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['sourceImageId', 'pixelSha256', 'width', 'height']);
    const sourceImageId = stringValue(input.sourceImageId, `${path}.sourceImageId`, 160);
    if (!isSourceImageId(sourceImageId)) {
        throw new FredrickProtocolParseError(`${path}.sourceImageId`, 'must be a canonical source image ID');
    }
    const pixelSha256 = stringValue(input.pixelSha256, `${path}.pixelSha256`, 80);
    if (!isSha256Digest(pixelSha256)) {
        throw new FredrickProtocolParseError(`${path}.pixelSha256`, 'must be a lowercase SHA-256 digest');
    }
    const width = positiveInteger(input.width, `${path}.width`);
    const height = positiveInteger(input.height, `${path}.height`);
    const parsed = {
        sourceImageId,
        pixelSha256,
        width,
        height,
    };
    if (createSourceImageId(parsed) !== sourceImageId) {
        throw new FredrickProtocolParseError(`${path}.sourceImageId`, 'does not match width, height, and pixelSha256');
    }
    return parsed;
}
export function parseFredrickSourceImageV1(value) {
    return parseSourceImage(value, '$');
}
export function parseFredrickSearchClaimV1(value) {
    const input = record(value, '$');
    exactKeys(input, '$', ['schemaVersion', 'claimId', 'repositoryId', 'searchId', 'sourceImageIds'], ['parentClaimId']);
    const claimId = stringValue(input.claimId, '$.claimId', 128);
    const searchId = stringValue(input.searchId, '$.searchId', 128);
    const parentClaimId = optionalString(input.parentClaimId, '$.parentClaimId', 128);
    if (!isFredrickSearchClaimId(claimId)) {
        throw new FredrickProtocolParseError('$.claimId', 'must be a canonical search claim ID');
    }
    if (!isFredrickSearchClaimId(searchId)) {
        throw new FredrickProtocolParseError('$.searchId', 'must be a canonical root search claim ID');
    }
    if (parentClaimId !== undefined && !isFredrickSearchClaimId(parentClaimId)) {
        throw new FredrickProtocolParseError('$.parentClaimId', 'must be a canonical search claim ID');
    }
    const sourceImageIds = arrayValue(input.sourceImageIds, '$.sourceImageIds').map((entry, index) => {
        const sourceImageId = stringValue(entry, `$.sourceImageIds[${index}]`, 160);
        if (!isSourceImageId(sourceImageId)) {
            throw new FredrickProtocolParseError(`$.sourceImageIds[${index}]`, 'must be a canonical source image ID');
        }
        return sourceImageId;
    });
    const claim = {
        schemaVersion: literal(input.schemaVersion, FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION, '$.schemaVersion'),
        claimId,
        repositoryId: stringValue(input.repositoryId, '$.repositoryId', 256),
        searchId,
        ...(parentClaimId === undefined ? {} : { parentClaimId }),
        sourceImageIds,
    };
    if (!fredrickSearchClaimIsCanonical(claim)) {
        throw new FredrickProtocolParseError('$', 'search claim is not canonical');
    }
    return claim;
}
function parseCapture(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['protocolVersion', 'sourceImage', 'observedAt', 'importedAt']);
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, `${path}.protocolVersion`),
        sourceImage: parseSourceImage(input.sourceImage, `${path}.sourceImage`),
        observedAt: safeInteger(input.observedAt, `${path}.observedAt`),
        importedAt: safeInteger(input.importedAt, `${path}.importedAt`),
    };
}
function parseOwlWindow(value, path, source) {
    const input = record(value, path);
    exactKeys(input, path, ['x', 'y', 'width', 'height']);
    const x = safeInteger(input.x, `${path}.x`);
    const y = safeInteger(input.y, `${path}.y`);
    const width = literal(input.width, OWL_WINDOW_WIDTH, `${path}.width`);
    const height = literal(input.height, OWL_WINDOW_HEIGHT, `${path}.height`);
    if (x + width > source.width || y + height > source.height) {
        throw new FredrickProtocolParseError(path, 'must fit within the full source image');
    }
    return { x, y, width, height };
}
function parseEvidence(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['evidenceImageId', 'pixelSha256', 'width', 'height']);
    const evidenceImageId = stringValue(input.evidenceImageId, `${path}.evidenceImageId`, 160);
    if (!isEvidenceImageId(evidenceImageId)) {
        throw new FredrickProtocolParseError(`${path}.evidenceImageId`, 'must be a canonical Owl evidence image ID');
    }
    const pixelSha256 = stringValue(input.pixelSha256, `${path}.pixelSha256`, 80);
    if (!isSha256Digest(pixelSha256)) {
        throw new FredrickProtocolParseError(`${path}.pixelSha256`, 'must be a lowercase SHA-256 digest');
    }
    const width = literal(input.width, OWL_WINDOW_WIDTH, `${path}.width`);
    const height = literal(input.height, OWL_WINDOW_HEIGHT, `${path}.height`);
    if (createEvidenceImageId(pixelSha256) !== evidenceImageId) {
        throw new FredrickProtocolParseError(`${path}.evidenceImageId`, 'does not match pixelSha256');
    }
    return { evidenceImageId, pixelSha256, width, height };
}
function parseTranscriber(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['owlContractVersion', 'implementation', 'implementationVersion']);
    return {
        owlContractVersion: literal(input.owlContractVersion, 1, `${path}.owlContractVersion`),
        implementation: stringValue(input.implementation, `${path}.implementation`, 128),
        implementationVersion: stringValue(input.implementationVersion, `${path}.implementationVersion`, 128),
    };
}
function parseValidator(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['owlValidationContractVersion', 'implementation', 'implementationVersion']);
    return {
        owlValidationContractVersion: literal(input.owlValidationContractVersion, 1, `${path}.owlValidationContractVersion`),
        implementation: stringValue(input.implementation, `${path}.implementation`, 128),
        implementationVersion: stringValue(input.implementationVersion, `${path}.implementationVersion`, 128),
    };
}
function parseNullableInteger(value, path, minimum) {
    return value === null ? null : safeInteger(value, path, minimum);
}
function parseListing(value, path) {
    const input = record(value, path);
    exactKeys(input, path, [
        'rowIndex',
        'sellerName',
        'storeName',
        'bundle',
        'price',
        'quantity',
    ]);
    const rowIndex = safeInteger(input.rowIndex, `${path}.rowIndex`);
    if (rowIndex >= OWL_LISTING_ROW_COUNT) {
        throw new FredrickProtocolParseError(`${path}.rowIndex`, `must be between 0 and ${OWL_LISTING_ROW_COUNT - 1}`);
    }
    return {
        rowIndex,
        sellerName: nullableString(input.sellerName, `${path}.sellerName`, 128),
        storeName: nullableString(input.storeName, `${path}.storeName`, 512),
        bundle: parseNullableInteger(input.bundle, `${path}.bundle`, 0),
        price: parseNullableInteger(input.price, `${path}.price`, 0),
        quantity: parseNullableInteger(input.quantity, `${path}.quantity`, 0),
    };
}
function parseTranscription(value, path) {
    const input = record(value, path);
    exactKeys(input, path, [
        'schemaVersion',
        'itemName',
        'page',
        'totalPages',
        'totalResults',
        'listings',
    ]);
    const itemName = nullableString(input.itemName, `${path}.itemName`, 512);
    const page = parseNullableInteger(input.page, `${path}.page`, 1);
    const totalPages = parseNullableInteger(input.totalPages, `${path}.totalPages`, 1);
    const totalResults = parseNullableInteger(input.totalResults, `${path}.totalResults`, 0);
    const listingsInput = arrayValue(input.listings, `${path}.listings`);
    if (listingsInput.length > OWL_LISTING_ROW_COUNT) {
        throw new FredrickProtocolParseError(`${path}.listings`, `must contain at most ${OWL_LISTING_ROW_COUNT} rows`);
    }
    const listings = listingsInput.map((entry, index) => parseListing(entry, `${path}.listings[${index}]`));
    const rows = new Set();
    for (const listing of listings) {
        if (rows.has(listing.rowIndex)) {
            throw new FredrickProtocolParseError(`${path}.listings`, `contains duplicate rowIndex ${listing.rowIndex}`);
        }
        rows.add(listing.rowIndex);
    }
    if (page !== null && totalPages !== null && page > totalPages) {
        throw new FredrickProtocolParseError(`${path}.page`, 'must not exceed totalPages');
    }
    if (totalResults !== null && totalPages !== null) {
        const expectedPages = expectedOwlPageCount(totalResults);
        if (totalPages !== expectedPages) {
            throw new FredrickProtocolParseError(`${path}.totalPages`, `must equal ${expectedPages} for totalResults=${totalResults}`);
        }
    }
    if (totalResults !== null && page !== null) {
        const expectedRows = expectedOwlRowsOnPage(totalResults, page);
        if (listings.length !== expectedRows) {
            throw new FredrickProtocolParseError(`${path}.listings`, `must contain ${expectedRows} occupied rows for page ${page}`);
        }
    }
    return {
        schemaVersion: literal(input.schemaVersion, OWL_TRANSCRIPTION_SCHEMA_VERSION, `${path}.schemaVersion`),
        itemName,
        page,
        totalPages,
        totalResults,
        listings,
    };
}
function parseRevisionCore(input, path) {
    const revisionId = stringValue(input.revisionId, `${path}.revisionId`, 256);
    const supersedesRevisionId = optionalString(input.supersedesRevisionId, `${path}.supersedesRevisionId`, 256);
    if (supersedesRevisionId === revisionId) {
        throw new FredrickProtocolParseError(`${path}.supersedesRevisionId`, 'must differ from revisionId');
    }
    const capture = parseCapture(input.capture, `${path}.capture`);
    const transcription = parseTranscription(input.transcription, `${path}.transcription`);
    const partial = booleanValue(input.partial, `${path}.partial`);
    if (partial !== transcriptionIsPartial(transcription)) {
        throw new FredrickProtocolParseError(`${path}.partial`, 'does not match the transcription null state');
    }
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, `${path}.protocolVersion`),
        revisionId,
        ...(supersedesRevisionId === undefined ? {} : { supersedesRevisionId }),
        capture,
        owlWindow: parseOwlWindow(input.owlWindow, `${path}.owlWindow`, capture.sourceImage),
        evidence: parseEvidence(input.evidence, `${path}.evidence`),
        transcriber: parseTranscriber(input.transcriber, `${path}.transcriber`),
        transcribedAt: safeInteger(input.transcribedAt, `${path}.transcribedAt`),
        transcription,
        partial,
    };
}
export function parseFredrickOwlPageCandidateV1(value) {
    const path = '$';
    const input = record(value, path);
    exactKeys(input, path, [
        'protocolVersion',
        'recordType',
        'revisionId',
        'capture',
        'owlWindow',
        'evidence',
        'transcriber',
        'transcribedAt',
        'transcription',
        'partial',
    ], ['supersedesRevisionId']);
    return {
        ...parseRevisionCore(input, path),
        recordType: literal(input.recordType, FREDRICK_OWL_PAGE_CANDIDATE_TYPE, '$.recordType'),
    };
}
function parseValidation(value, path) {
    const input = record(value, path);
    exactKeys(input, path, ['policy', 'validator', 'validatedAt']);
    return {
        policy: literal(input.policy, FREDRICK_MARKET_VALIDATION_POLICY, `${path}.policy`),
        validator: parseValidator(input.validator, `${path}.validator`),
        validatedAt: safeInteger(input.validatedAt, `${path}.validatedAt`),
    };
}
function parsePageRevision(value, path) {
    const input = record(value, path);
    exactKeys(input, path, [
        'protocolVersion',
        'recordType',
        'revisionId',
        'capture',
        'owlWindow',
        'evidence',
        'transcriber',
        'transcribedAt',
        'transcription',
        'partial',
        'validation',
        'publishedAt',
    ], ['supersedesRevisionId']);
    const core = parseRevisionCore(input, path);
    return {
        ...core,
        recordType: literal(input.recordType, FREDRICK_OWL_PAGE_REVISION_TYPE, `${path}.recordType`),
        validation: parseValidation(input.validation, `${path}.validation`),
        publishedAt: safeInteger(input.publishedAt, `${path}.publishedAt`),
    };
}
export function parseFredrickOwlPageRevisionV1(value) {
    return parsePageRevision(value, '$');
}
function parseSearchClaimAt(value, path) {
    try {
        return parseFredrickSearchClaimV1(value);
    }
    catch (error) {
        if (error instanceof FredrickProtocolParseError) {
            throw new FredrickProtocolParseError(`${path}${error.path.slice(1)}`, error.message.slice(error.path.length + 2));
        }
        throw error;
    }
}
export function parseFredrickPublishedRecordEnvelopeV1(value) {
    const path = '$';
    const input = record(value, path);
    exactKeys(input, path, ['protocolVersion', 'recordType', 'sequence', 'record']);
    const recordValue = record(input.record, '$.record');
    const publishedRecord = Object.hasOwn(recordValue, 'claimId')
        ? parseSearchClaimAt(recordValue, '$.record')
        : parsePageRevision(recordValue, '$.record');
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, '$.protocolVersion'),
        recordType: literal(input.recordType, FREDRICK_PUBLISHED_ENVELOPE_TYPE, '$.recordType'),
        sequence: positiveInteger(input.sequence, '$.sequence'),
        record: publishedRecord,
    };
}
const alreadyKnownReasons = new Set(['revision', 'source-image']);
const rejectionCodes = new Set([
    'invalid-candidate',
    'invalid-evidence',
    'transcription-mismatch',
    'unsupported-protocol',
    'unsupported-transcriber',
    'supersession-conflict',
]);
const claimRejectionCodes = new Set([
    'invalid-claim',
    'unknown-source',
    'missing-parent',
    'parent-mismatch',
]);
export function parseFredrickPushV1(value) {
    const input = record(value, '$');
    exactKeys(input, '$', ['protocolVersion', 'pushId', 'baseHighWatermark', 'pages', 'claims']);
    const pages = arrayValue(input.pages, '$.pages').map((entry, index) => {
        try {
            return parseFredrickOwlPageCandidateV1(entry);
        }
        catch (error) {
            if (error instanceof FredrickProtocolParseError) {
                throw new FredrickProtocolParseError(`$.pages[${index}]${error.path.slice(1)}`, error.message.slice(error.path.length + 2));
            }
            throw error;
        }
    });
    const claims = arrayValue(input.claims, '$.claims').map((entry, index) => parseSearchClaimAt(entry, `$.claims[${index}]`));
    const revisionIds = new Set();
    for (const page of pages) {
        if (revisionIds.has(page.revisionId)) {
            throw new FredrickProtocolParseError('$.pages', `contains duplicate revisionId ${page.revisionId}`);
        }
        revisionIds.add(page.revisionId);
    }
    const claimIds = new Set();
    for (const claim of claims) {
        if (claimIds.has(claim.claimId)) {
            throw new FredrickProtocolParseError('$.claims', `contains duplicate claimId ${claim.claimId}`);
        }
        claimIds.add(claim.claimId);
    }
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, '$.protocolVersion'),
        pushId: stringValue(input.pushId, '$.pushId', 256),
        baseHighWatermark: safeInteger(input.baseHighWatermark, '$.baseHighWatermark'),
        pages,
        claims,
    };
}
export function parseFredrickPushResultV1(value) {
    const input = record(value, '$');
    exactKeys(input, '$', [
        'protocolVersion',
        'pushId',
        'receiptId',
        'staged',
        'alreadyKnown',
        'rejected',
        'stagedClaims',
        'alreadyKnownClaims',
        'rejectedClaims',
    ]);
    const staged = arrayValue(input.staged, '$.staged').map((entry, index) => stringValue(entry, `$.staged[${index}]`, 256));
    const alreadyKnown = arrayValue(input.alreadyKnown, '$.alreadyKnown').map((entry, index) => {
        const path = `$.alreadyKnown[${index}]`;
        const row = record(entry, path);
        exactKeys(row, path, [
            'candidateRevisionId',
            'remoteRevisionId',
            'sourceImageId',
            'reason',
        ]);
        const sourceImageId = stringValue(row.sourceImageId, `${path}.sourceImageId`, 160);
        if (!isSourceImageId(sourceImageId)) {
            throw new FredrickProtocolParseError(`${path}.sourceImageId`, 'must be a canonical source image ID');
        }
        const reason = stringValue(row.reason, `${path}.reason`, 32);
        if (!alreadyKnownReasons.has(reason)) {
            throw new FredrickProtocolParseError(`${path}.reason`, 'is not a protocol v1 already-known reason');
        }
        return {
            candidateRevisionId: stringValue(row.candidateRevisionId, `${path}.candidateRevisionId`, 256),
            remoteRevisionId: stringValue(row.remoteRevisionId, `${path}.remoteRevisionId`, 256),
            sourceImageId,
            reason: reason,
        };
    });
    const rejected = arrayValue(input.rejected, '$.rejected').map((entry, index) => {
        const path = `$.rejected[${index}]`;
        const row = record(entry, path);
        exactKeys(row, path, ['candidateRevisionId', 'code'], ['message']);
        const code = stringValue(row.code, `${path}.code`, 64);
        if (!rejectionCodes.has(code)) {
            throw new FredrickProtocolParseError(`${path}.code`, 'is not a protocol v1 rejection code');
        }
        return {
            candidateRevisionId: stringValue(row.candidateRevisionId, `${path}.candidateRevisionId`, 256),
            code: code,
            ...(row.message === undefined ? {} : { message: stringValue(row.message, `${path}.message`, 1024) }),
        };
    });
    const parseClaimIdArray = (value, path) => arrayValue(value, path).map((entry, index) => {
        const claimId = stringValue(entry, `${path}[${index}]`, 128);
        if (!isFredrickSearchClaimId(claimId)) {
            throw new FredrickProtocolParseError(`${path}[${index}]`, 'must be a canonical search claim ID');
        }
        return claimId;
    });
    const stagedClaims = parseClaimIdArray(input.stagedClaims, '$.stagedClaims');
    const alreadyKnownClaims = parseClaimIdArray(input.alreadyKnownClaims, '$.alreadyKnownClaims');
    const rejectedClaims = arrayValue(input.rejectedClaims, '$.rejectedClaims').map((entry, index) => {
        const path = `$.rejectedClaims[${index}]`;
        const row = record(entry, path);
        exactKeys(row, path, ['claimId', 'code'], ['message']);
        const claimId = stringValue(row.claimId, `${path}.claimId`, 128);
        if (!isFredrickSearchClaimId(claimId)) {
            throw new FredrickProtocolParseError(`${path}.claimId`, 'must be a canonical search claim ID');
        }
        const code = stringValue(row.code, `${path}.code`, 64);
        if (!claimRejectionCodes.has(code)) {
            throw new FredrickProtocolParseError(`${path}.code`, 'is not a protocol v1 claim rejection code');
        }
        return {
            claimId,
            code: code,
            ...(row.message === undefined ? {} : { message: stringValue(row.message, `${path}.message`, 1024) }),
        };
    });
    const pageOutcomes = new Set();
    for (const revisionId of [
        ...staged,
        ...alreadyKnown.map((entry) => entry.candidateRevisionId),
        ...rejected.map((entry) => entry.candidateRevisionId),
    ]) {
        if (pageOutcomes.has(revisionId)) {
            throw new FredrickProtocolParseError('$', `revisionId ${revisionId} appears in more than one push outcome`);
        }
        pageOutcomes.add(revisionId);
    }
    const claimOutcomes = new Set();
    for (const claimId of [
        ...stagedClaims,
        ...alreadyKnownClaims,
        ...rejectedClaims.map((entry) => entry.claimId),
    ]) {
        if (claimOutcomes.has(claimId)) {
            throw new FredrickProtocolParseError('$', `claimId ${claimId} appears in more than one push outcome`);
        }
        claimOutcomes.add(claimId);
    }
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, '$.protocolVersion'),
        pushId: stringValue(input.pushId, '$.pushId', 256),
        receiptId: stringValue(input.receiptId, '$.receiptId', 256),
        staged,
        alreadyKnown,
        rejected,
        stagedClaims,
        alreadyKnownClaims,
        rejectedClaims,
    };
}
function parseSegment(value, path) {
    const input = record(value, path);
    exactKeys(input, path, [
        'segmentId',
        'location',
        'minSequence',
        'maxSequence',
        'recordCount',
        'byteLength',
        'sha256',
        'format',
    ]);
    const segmentId = stringValue(input.segmentId, `${path}.segmentId`, 80);
    if (!isSha256Digest(segmentId)) {
        throw new FredrickProtocolParseError(`${path}.segmentId`, 'must be a lowercase SHA-256 digest');
    }
    const sha256 = stringValue(input.sha256, `${path}.sha256`, 80);
    if (!isSha256Digest(sha256)) {
        throw new FredrickProtocolParseError(`${path}.sha256`, 'must be a lowercase SHA-256 digest');
    }
    const minSequence = positiveInteger(input.minSequence, `${path}.minSequence`);
    const maxSequence = positiveInteger(input.maxSequence, `${path}.maxSequence`);
    if (maxSequence < minSequence) {
        throw new FredrickProtocolParseError(`${path}.maxSequence`, 'must be greater than or equal to minSequence');
    }
    const recordCount = positiveInteger(input.recordCount, `${path}.recordCount`);
    if (recordCount > maxSequence - minSequence + 1) {
        throw new FredrickProtocolParseError(`${path}.recordCount`, 'cannot exceed the declared sequence range');
    }
    const formatInput = record(input.format, `${path}.format`);
    exactKeys(formatInput, `${path}.format`, ['envelope', 'mediaType'], ['contentEncoding']);
    return {
        segmentId,
        location: stringValue(input.location, `${path}.location`, 2048),
        minSequence,
        maxSequence,
        recordCount,
        byteLength: positiveInteger(input.byteLength, `${path}.byteLength`),
        sha256,
        format: {
            envelope: literal(formatInput.envelope, 'fredrick-published-record-v1', `${path}.format.envelope`),
            mediaType: stringValue(formatInput.mediaType, `${path}.format.mediaType`, 256),
            ...(formatInput.contentEncoding === undefined
                ? {}
                : { contentEncoding: stringValue(formatInput.contentEncoding, `${path}.format.contentEncoding`, 128) }),
        },
    };
}
export function parseFredrickSegmentV1(value) {
    return parseSegment(value, '$');
}
export function parseFredrickSnapshotV1(value) {
    const path = '$';
    const input = record(value, path);
    exactKeys(input, path, [
        'protocolVersion',
        'recordType',
        'snapshotId',
        'libraryId',
        'generation',
        'highWatermark',
        'segments',
        'createdAt',
    ], ['parentSnapshotId']);
    const snapshotId = stringValue(input.snapshotId, '$.snapshotId', 80);
    if (!isSha256Digest(snapshotId)) {
        throw new FredrickProtocolParseError('$.snapshotId', 'must be a lowercase SHA-256 digest');
    }
    const parentSnapshotId = optionalString(input.parentSnapshotId, '$.parentSnapshotId', 80);
    if (parentSnapshotId !== undefined && !isSha256Digest(parentSnapshotId)) {
        throw new FredrickProtocolParseError('$.parentSnapshotId', 'must be a lowercase SHA-256 digest');
    }
    const highWatermark = safeInteger(input.highWatermark, '$.highWatermark');
    const segments = arrayValue(input.segments, '$.segments').map((entry, index) => parseSegment(entry, `$.segments[${index}]`));
    const ids = new Set();
    const sorted = [...segments].sort((left, right) => left.minSequence - right.minSequence);
    let previousMax = 0;
    for (const segment of sorted) {
        if (ids.has(segment.segmentId)) {
            throw new FredrickProtocolParseError('$.segments', `contains duplicate segmentId ${segment.segmentId}`);
        }
        ids.add(segment.segmentId);
        if (segment.minSequence <= previousMax) {
            throw new FredrickProtocolParseError('$.segments', 'contains overlapping sequence ranges');
        }
        if (segment.maxSequence > highWatermark) {
            throw new FredrickProtocolParseError('$.segments', 'contains a sequence above highWatermark');
        }
        previousMax = segment.maxSequence;
    }
    if ((segments.length === 0 && highWatermark !== 0)
        || (segments.length > 0 && previousMax !== highWatermark)) {
        throw new FredrickProtocolParseError('$.highWatermark', 'must equal the highest active segment sequence');
    }
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, '$.protocolVersion'),
        recordType: literal(input.recordType, FREDRICK_SNAPSHOT_TYPE, '$.recordType'),
        snapshotId,
        libraryId: stringValue(input.libraryId, '$.libraryId', 256),
        generation: positiveInteger(input.generation, '$.generation'),
        ...(parentSnapshotId === undefined ? {} : { parentSnapshotId }),
        highWatermark,
        segments,
        createdAt: safeInteger(input.createdAt, '$.createdAt'),
    };
}
export function parseFredrickLatestRefV1(value) {
    const path = '$';
    const input = record(value, path);
    exactKeys(input, path, [
        'protocolVersion',
        'recordType',
        'libraryId',
        'snapshotId',
        'snapshotLocation',
        'generation',
        'highWatermark',
        'updatedAt',
    ]);
    const snapshotId = stringValue(input.snapshotId, '$.snapshotId', 80);
    if (!isSha256Digest(snapshotId)) {
        throw new FredrickProtocolParseError('$.snapshotId', 'must be a lowercase SHA-256 digest');
    }
    return {
        protocolVersion: literal(input.protocolVersion, FREDRICK_DATA_PROTOCOL_VERSION, '$.protocolVersion'),
        recordType: literal(input.recordType, FREDRICK_LATEST_REF_TYPE, '$.recordType'),
        libraryId: stringValue(input.libraryId, '$.libraryId', 256),
        snapshotId,
        snapshotLocation: stringValue(input.snapshotLocation, '$.snapshotLocation', 2048),
        generation: positiveInteger(input.generation, '$.generation'),
        highWatermark: safeInteger(input.highWatermark, '$.highWatermark'),
        updatedAt: safeInteger(input.updatedAt, '$.updatedAt'),
    };
}
//# sourceMappingURL=parse.js.map